import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Minus } from 'lucide-react';
import { currencies, getCurrencyByCode, formatCurrency } from '@/lib/currencies';

type UserProfile = {
  id: string;
  email: string | null;
  phone: string | null;
  balance: number;
  kyc_status: string;
  role: string;
  created_at: string;
  currency: string;
};

export default function UserManagement() {
  const { t, language } = useLanguage();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'add' | 'remove'>('add');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('role', 'user')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBalanceAdjustment = async () => {
    if (!selectedUser) return;

    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (!selectedCurrency) {
      toast.error('Please select a currency');
      return;
    }
    try {
      const newBalance = actionType === 'add'
        ? selectedUser.balance + parsedAmount
        : selectedUser.balance - parsedAmount;

      if (newBalance < 0) {
        toast.error('Insufficient balance');
        return;
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ balance: newBalance, currency: selectedCurrency })
        .eq('id', selectedUser.id);

      if (updateError) throw updateError;

      const { error: transactionError } = await supabase
        .from('transactions')
        .insert({
          user_id: selectedUser.id,
          type: 'admin_adjustment',
          amount: parsedAmount * (actionType === 'add' ? 1 : -1),
          status: 'completed',
          admin_note: note || `Admin ${actionType === 'add' ? 'added' : 'removed'} balance`,
          currency: selectedCurrency,
        });

      if (transactionError) throw transactionError;

      toast.success(`Balance ${actionType === 'add' ? 'added' : 'removed'} successfully!`);
      setDialogOpen(false);
      setAmount('');
      setNote('');
      setSelectedCurrency('');
      fetchUsers();
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const openDialog = (user: UserProfile, type: 'add' | 'remove') => {
    setSelectedUser(user);
    setActionType(type);
    setSelectedCurrency(user.currency || 'USD');
    setDialogOpen(true);
  };

  const getKycStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-500';
      case 'rejected':
        return 'bg-red-500';
      case 'pending':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('users')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {users.map((user) => (
            <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg">
              <div className="space-y-1">
                <div className="font-medium">{user.email || user.phone}</div>
                <div className="text-sm text-muted-foreground">
                  Balance: {formatCurrency(user.balance, user.currency || 'USD', language)}
                </div>
                <Badge className={getKycStatusColor(user.kyc_status)}>
                  KYC: {t(user.kyc_status)}
                </Badge>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openDialog(user, 'add')}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('addBalance')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openDialog(user, 'remove')}
                >
                  <Minus className="h-4 w-4 mr-2" />
                  {t('removeBalance')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{actionType === 'add' ? t('addBalance') : t('removeBalance')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('currency')}</Label>
              <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t('selectCurrency')} />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {currencies.map((currency) => (
                    <SelectItem key={currency.code} value={currency.code}>
                      <span className="flex items-center gap-2">
                        <span>{currency.flag}</span>
                        <span>
                          {currency.code} - {language === 'ar' ? currency.nameAr : currency.name}
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('amount')}</Label>
              <Input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label>Note</Label>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Optional note"
              />
            </div>
            <Button onClick={handleBalanceAdjustment} className="w-full">
              Confirm
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}